import React, { useState } from 'react';
import { Plus, RefreshCw, Trash2, Copy, Check, Eye, EyeOff } from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import apiKeysService from '../services/apiKeys';
import { useApps } from '../contexts/AppsContext';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import { Button } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import Modal from '../components/common/Modal';
import DataTable, { RowAction, RowActions } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import { STATUS_OPTIONS } from '../components/common/filters';
import useServerTable from '../hooks/useServerTable';
import { Field } from '../components/ui/Field';
import { TextInput } from '../components/ui/Input';

const ApiKeyIcon = ENTITY_ICONS.apiKeys;

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const ApiKeysPage = () => {
    const { loading: loadingApps, selectedAppId: appId } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newKeyData, setNewKeyData] = useState(null); // shown after creation/rotation
    const [formData, setFormData] = useState({ key_name: '', expires_in_days: '' });
    const [copiedField, setCopiedField] = useState('');
    const [showSecret, setShowSecret] = useState(false);

    // URL keys: status, expiry, last_used_from/to, created_from/to.
    const columns = [
        {
            key: 'key_name',
            header: 'Key',
            sortKey: 'name',
            locked: true,
            render: (k) => (
                <div className="flex items-center gap-3 min-w-0">
                    <div className="min-w-0">
                        <div className="font-medium text-slate-900">{k.key_name || 'Unnamed Key'}</div>
                        <code className="text-xs text-slate-500 font-mono">{k.api_key?.substring(0, 16)}...</code>
                    </div>
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (k) => <StatusBadge active={Boolean(k.is_active)} />,
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'created_on',
            header: 'Created',
            sortKey: 'created',
            render: (k) => formatDate(k.created_on),
            filter: { key: 'created', type: 'date' },
        },
        {
            key: 'last_used_at',
            header: 'Last used',
            sortKey: 'last_used',
            render: (k) => formatDate(k.last_used_at),
            filter: { key: 'last_used', type: 'date' },
        },
        {
            key: 'expires_at',
            header: 'Expires',
            sortKey: 'expires',
            render: (k) => formatDate(k.expires_at),
            // expiry values are named predicates on the API (controllers/apiKeys.js), matching the
            // dashboard's "expired" and "expire within 14 days" signals.
            filter: {
                key: 'expiry',
                type: 'multiselect',
                defaultExpanded: true,
                options: [
                    { value: 'expired', label: 'Expired' },
                    { value: 'expiring', label: 'Expires within 14 days' },
                    { value: 'none', label: 'Never expires' },
                ],
            },
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (k) =>
                k.is_active ? (
                    <RowActions>
                        <RowAction icon={RefreshCw} label="Rotate key" onClick={() => handleRotate(k)} />
                        <RowAction icon={Trash2} label="Revoke key" tone="danger" onClick={() => handleRevoke(k)} />
                    </RowActions>
                ) : null,
        },
    ];

    const table = useServerTable((query) => apiKeysService.getPage(appId, query), {
        deps: [appId],
        enabled: Boolean(appId),
        defaultSort: { key: 'created', dir: 'desc' },
        columns,
    });

    const handleCreate = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const payload = { key_name: formData.key_name || undefined };
            if (formData.expires_in_days) payload.expires_in_days = parseInt(formData.expires_in_days);
            const res = await apiKeysService.create(appId, payload);
            setNewKeyData(res.data);
            setShowCreateModal(false);
            setFormData({ key_name: '', expires_in_days: '' });
            table.reload();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to create API key');
        }
    };

    const keyLabel = (k) => `"${k.key_name || 'Unnamed Key'}"`;

    const handleRotate = async (k) => {
        const ok = await confirm({
            title: `Rotate API key ${k.key_name || 'Unnamed key'}?`,
            body: 'A new key and secret are issued. The current key and secret stop working immediately, so update every service that uses them.',
            confirmLabel: 'Rotate',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            const res = await apiKeysService.rotate(k.api_key_id);
            setNewKeyData(res.data);
            table.reload();
            notice.success(`API key ${keyLabel(k)} rotated. Copy the new secret below.`);
        } catch (err) {
            notice.error(err, `Couldn’t rotate API key ${keyLabel(k)}. Try again.`);
        }
    };

    const handleRevoke = async (k) => {
        const ok = await confirm({
            title: `Revoke API key ${k.key_name || 'Unnamed key'}?`,
            body: 'Requests signed with this key are rejected immediately. A revoked key can’t be restored.',
            confirmLabel: 'Revoke',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            await apiKeysService.remove(k.api_key_id);
            table.reload();
            notice.success(`API key ${keyLabel(k)} revoked.`);
        } catch (err) {
            notice.error(err, `Couldn’t revoke API key ${keyLabel(k)}. Try again.`);
        }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(''), 2000);
    };


    return (
        <div className="space-y-4">
            <PageHeader
                title="API Keys"
                actions={
                    appId && (
                        <Button
                            onClick={() => {
                                setFormData({ key_name: '', expires_in_days: '' });
                                setError('');
                                setShowCreateModal(true);
                            }}
                        >
                            <Plus className="h-4 w-4" aria-hidden="true" /> Generate Key
                        </Button>
                    )
                }
            />

            {/* New Key Display (shown after create/rotate) */}
            {newKeyData && (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-5 space-y-3">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                        <ApiKeyIcon className="h-4 w-4" aria-hidden="true" />
                        Save these credentials — the secret will not be shown again!
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500 w-20">API Key:</span>
                            <code className="flex-1 bg-white rounded px-2 py-1 text-sm font-mono border border-amber-200">
                                {newKeyData.api_key}
                            </code>
                            <Tooltip label={copiedField === 'key' ? 'Copied' : 'Copy API key'}>
                                <button
                                    onClick={() => copyToClipboard(newKeyData.api_key, 'key')}
                                    aria-label="Copy API key"
                                    className="p-1 rounded hover:bg-amber-100"
                                >
                                    {copiedField === 'key' ? (
                                        <Check className="h-4 w-4 text-emerald-600" />
                                    ) : (
                                        <Copy className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>
                            </Tooltip>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500 w-20">Secret:</span>
                            <code className="flex-1 bg-white rounded px-2 py-1 text-sm font-mono border border-amber-200">
                                {showSecret ? newKeyData.api_secret : '•'.repeat(40)}
                            </code>
                            <Tooltip label={showSecret ? 'Hide secret' : 'Show secret'}>
                                <button
                                    onClick={() => setShowSecret(!showSecret)}
                                    aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                                    className="p-1 rounded hover:bg-amber-100"
                                >
                                    {showSecret ? (
                                        <EyeOff className="h-4 w-4 text-slate-500" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>
                            </Tooltip>
                            <Tooltip label={copiedField === 'secret' ? 'Copied' : 'Copy secret'}>
                                <button
                                    onClick={() => copyToClipboard(newKeyData.api_secret, 'secret')}
                                    aria-label="Copy secret"
                                    className="p-1 rounded hover:bg-amber-100"
                                >
                                    {copiedField === 'secret' ? (
                                        <Check className="h-4 w-4 text-emerald-600" />
                                    ) : (
                                        <Copy className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                    <button onClick={() => setNewKeyData(null)} className="text-xs text-amber-700 hover:underline">
                        Dismiss
                    </button>
                </div>
            )}

            {loadingApps ? (
                <SkeletonList />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
                    <p className="text-slate-600">Choose an app in the sidebar to see its API keys.</p>
                </div>
            ) : (
                <DataTable
                    {...table.tableProps}
                    columns={columns}
                    tableKey="api-keys"
                    rowKey="api_key_id"
                    searchPlaceholder="Search name or key…"
                    emptyMessage="No API keys yet. Generate one to enable basic auth."
                />
            )}

            {/* Create Key Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Generate API Key"
                size="sm"
            >
                <form onSubmit={handleCreate} className="space-y-4">
                    <Field label="Key Name (optional)">
                        <TextInput
                            type="text"
                            value={formData.key_name}
                            onChange={(e) => setFormData({ ...formData, key_name: e.target.value })}
                            placeholder="e.g., Production API"
                        />
                    </Field>
                    <Field label="Expires in (days, optional)">
                        <TextInput
                            type="number"
                            min="1"
                            value={formData.expires_in_days}
                            onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                            placeholder="Leave empty for no expiry"
                        />
                    </Field>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Generate
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default ApiKeysPage;
